import { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MEMBER_RULES } from "@/constants/testIds";
import { fetchRewardRules } from "@/lib/api";

const SCHEDULE_LABELS = {
  customer_referral: "Customer Referral",
  ambassador: "Ambassador",
  trade_partner: "Trade Partner",
};

const inr = (value) => `INR ${Number(value).toLocaleString("en-IN")}`;

const positionLabel = ({ from_position: from, to_position: to }) => {
  if (to == null) return `Position ${from} onward`;
  return `Positions ${from} to ${to}`;
};

const capacityLabel = (bands, index) => {
  const band = bands[index];
  if (band.max_kw === null) return `Above ${bands[index - 1].max_kw} kW`;
  if (index === 0) return `Up to ${band.max_kw} kW`;
  return `Above ${bands[index - 1].max_kw} kW, up to ${band.max_kw} kW`;
};

const ResidentialRules = ({ rules }) => {
  if (rules.schedule === "customer_referral") {
    return (
      <p className="text-sm text-brand-ink">
        {inr(rules.residential[0].fee)} per commissioned residential referral
      </p>
    );
  }
  return (
    <table className="w-full text-left text-sm">
      <thead className="text-xs uppercase tracking-wide text-brand-subtle">
        <tr><th className="pb-2">Rolling position</th><th className="pb-2">Reward</th></tr>
      </thead>
      <tbody>
        {rules.residential.map((step) => (
          <tr key={step.from_position} className="border-t border-black/5">
            <td className="py-2">{positionLabel(step)}</td>
            <td className="py-2 font-semibold">{inr(step.fee)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const CommercialRules = ({ bands }) => (
  <table className="w-full text-left text-sm">
    <thead className="text-xs uppercase tracking-wide text-brand-subtle">
      <tr><th className="pb-2">System size</th><th className="pb-2">Reward</th></tr>
    </thead>
    <tbody>
      {bands.map((band, index) => (
        <tr key={band.max_kw ?? "above"} className="border-t border-black/5">
          <td className="py-2">{capacityLabel(bands, index)}</td>
          <td className="py-2">
            <span className="font-semibold">{inr(band.fee)}</span>
            {band.held && (
              <span className="block text-xs text-brand-muted">
                held for deal desk review
              </span>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const RuleDetails = ({ rules }) => {
  if (rules.launched === false) {
    return (
      <p className="rounded-xl bg-brand-gold-soft p-4 text-sm leading-relaxed text-brand-ink">
        The Trade Partner programme opens later. Commissioned referrals are
        recorded and held until Greentek publishes the Trade Partner schedule.
      </p>
    );
  }
  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <h3 className="font-semibold text-brand-ink">Residential</h3>
        <ResidentialRules rules={rules} />
      </section>
      <section className="space-y-2">
        <h3 className="font-semibold text-brand-ink">Commercial</h3>
        <CommercialRules bands={rules.commercial} />
      </section>
      <div className="space-y-2 rounded-xl bg-brand-surface p-4 text-sm leading-relaxed text-brand-muted">
        <p>
          {Number(rules.tds.rate_with_pan)} percent with PAN on file, {Number(rules.tds.rate_without_pan)} percent without, once your rewards in a year pass {inr(rules.tds.annual_threshold)}
        </p>
        <p>Paid after the installation is commissioned in ERP and approved by Greentek.</p>
        <p>
          Links are valid {rules.share_validity_days} days from sharing, first code wins, no self-referral.
        </p>
      </div>
    </div>
  );
};

export const RewardRulesDialog = ({ children, open: controlledOpen, onOpenChange }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const changeOpen = onOpenChange ?? setInternalOpen;

  useEffect(() => {
    if (open === false) return undefined;
    let active = true;
    setError(false);
    setLoading(true);
    fetchRewardRules()
      .then((payload) => {
        if (active) setRules(payload);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent
        className="max-h-[85vh] max-w-2xl overflow-y-auto"
        data-testid={MEMBER_RULES.dialog}
      >
        <DialogHeader>
          <DialogTitle>How rewards work</DialogTitle>
          <DialogDescription>
            {rules
              ? `${SCHEDULE_LABELS[rules.schedule]} · ${rules.version}`
              : "Your current published reward schedule"}
          </DialogDescription>
        </DialogHeader>
        {loading && <p className="text-sm text-brand-muted">Loading reward rules…</p>}
        {error && <p className="text-sm text-red-700">Could not load reward rules.</p>}
        {rules && <RuleDetails rules={rules} />}
      </DialogContent>
    </Dialog>
  );
};
